import { ReportItem } from 'store/ducks/Reporting/types';
import * as Styled from './BusinessProcessTree.styled';
import { useTranslation } from 'react-i18next';
import { stripHtmlTags } from 'utils/stripHtmlTags';

export type InfoType =
  | 'process'
  | 'risk'
  | 'control'
  | 'gap'
  | 'audit'
  | 'solution'
  | 'bru'
  | 'evidence'
  | 'processL1'
  | 'processL2'
  | 'processL3'
  | 'processL4'
  | 'controlEffectiveness';

const infoConfig = (
  t: any
): Record<
  InfoType,
  {
    label: string;
    fields: {
      label: string;
      key: string;
      transform?: (value: any) => any;
    }[];
  }
> => ({
  process: {
    label: t('info.process.label'),
    fields: [
      { label: t('info.process.type'), key: 'process_type_name' },
      { label: t('info.process.criticality'), key: 'process_criticality_name' }
    ]
  },

  risk: {
    label: t('info.risk.label'),
    fields: [
      { label: t('info.risk.impact'), key: 'risk_instance_impact_consequence' },
      {
        label: t('info.risk.probability'),
        key: 'risk_instance_probability_of_occurrence_name'
      },
      { label: t('info.risk.severity'), key: 'risk_instance_severity_name' }
    ]
  },

  gap: {
    label: t('info.gap.label'),
    fields: [
      {
        label: t('info.gap.description'),
        key: 'gap_description',
        transform: (v) => stripHtmlTags(v)
      },
      { label: t('info.gap.dueDate'), key: 'gap_due_date' },
      { label: t('info.gap.priority'), key: 'priority_name' },
      { label: t('info.gap.owner'), key: 'owner_name' },
      {
        label: t('info.gap.closed'),
        key: 'gap_closed',
        transform: (v) => (v ? t('common.yes') : t('common.no'))
      }
    ]
  },

  audit: {
    label: t('info.audit.label'),
    fields: [
      {
        label: t('info.audit.description'),
        key: 'afg_description',
        transform: (v) =>
          Array.isArray(v) ? v.map(stripHtmlTags).join(', ') : stripHtmlTags(v)
      },
      { label: t('info.audit.dueDate'), key: 'afg_due_date' },
      { label: t('info.audit.closed'), key: 'afg_closed' },
      { label: t('info.audit.ownerNotified'), key: 'afg_owner_notified' },
      { label: t('info.audit.priority'), key: 'afg_priority_name' },
      { label: t('info.audit.source'), key: 'afg_source_name' },
      { label: t('info.audit.owner'), key: 'afg_owner_name' }
    ]
  },

  solution: {
    label: t('info.solution.label'),
    fields: [
      {
        label: t('info.solution.description'),
        key: 'sol_description',
        transform: (v) => stripHtmlTags(v)
      },
      { label: t('info.solution.approved'), key: 'sol_approved' },
      { label: t('info.solution.approver'), key: 'sol_approver_name' },
      {
        label: t('info.solution.approverNotified'),
        key: 'sol_approver_notified'
      },
      { label: t('info.solution.evidenceRequired'), key: 'sol_evd_required' },
      { label: t('info.solution.reviewed'), key: 'sol_reviewed' },
      { label: t('info.solution.reviewer'), key: 'sol_reviewer_name' },
      {
        label: t('info.solution.reviewerNotified'),
        key: 'sol_reviewer_notified'
      },
      { label: t('info.solution.type'), key: 'sol_type_name' }
    ]
  },

  bru: {
    label: t('info.bru.label'),
    fields: [{ label: t('info.bru.name'), key: 'bru_key_value' }]
  },

  control: {
    label: t('info.control.label'),
    fields: [
      { label: t('info.control.frequency'), key: 'control_frequency' },
      {
        label: t('info.control.effectiveness'),
        key: 'control_instance_control_overall_effectiveness_name'
      },
      {
        label: t('info.control.status'),
        key: 'control_instance_control_status_name'
      },
      {
        label: t('info.control.performer'),
        key: 'control_instance_control_performer_name'
      }
    ]
  },

  evidence: {
    label: t('info.evidence.label'),
    fields: [
      {
        label: t('info.evidence.description'),
        key: 'evd_description',
        transform: (v) => stripHtmlTags(v)
      },
      { label: t('info.evidence.approved'), key: 'evd_approved' },
      { label: t('info.evidence.approver'), key: 'evd_approver_name' },
      { label: t('info.evidence.reviewed'), key: 'evd_reviewed' },
      { label: t('info.evidence.reviewer'), key: 'evd_reviewer_name' },
      { label: t('info.evidence.validFrom'), key: 'evd_valid_from' },
      { label: t('info.evidence.validTo'), key: 'evd_valid_to' },
      { label: t('info.evidence.createdBy'), key: 'evd_created_by' },
      { label: t('info.evidence.createdOn'), key: 'evd_created_on' }
    ]
  },

  processL1: {
    label: t('info.processL1.label'),
    fields: [
      {
        label: t('info.processL1.startDate'),
        key: 'business_process_l1_start_date'
      },
      {
        label: t('info.processL1.endDate'),
        key: 'business_process_l1_end_date'
      }
    ]
  },

  processL2: {
    label: t('info.processL2.label'),
    fields: [
      {
        label: t('info.processL2.startDate'),
        key: 'business_process_l2_start_date'
      },
      {
        label: t('info.processL2.endDate'),
        key: 'business_process_l2_end_date'
      }
    ]
  },

  processL3: {
    label: t('info.processL3.label'),
    fields: [
      {
        label: t('info.processL3.startDate'),
        key: 'business_process_l3_start_date'
      },
      {
        label: t('info.processL3.endDate'),
        key: 'business_process_l3_end_date'
      }
    ]
  },

  processL4: {
    label: t('info.processL4.label'),
    fields: [
      {
        label: t('info.processL4.startDate'),
        key: 'business_process_l4_start_date'
      },
      {
        label: t('info.processL4.endDate'),
        key: 'business_process_l4_end_date'
      }
    ]
  },

  controlEffectiveness: {
    label: t('info.controlEffectiveness.label'),
    fields: [
      {
        label: t('info.controlEffectiveness.description'),
        key: 'control_description'
      },
      { label: t('info.controlEffectiveness.owner'), key: 'owner_name' },
      {
        label: t('info.controlEffectiveness.keyControl'),
        key: 'key_control',
        transform: (v) =>
          v == null ? '-' : v ? t('common.yes') : t('common.no')
      },
      {
        label: t('info.controlEffectiveness.controlType'),
        key: 'control_type_name'
      },
      {
        label: t('info.controlEffectiveness.control1OverallEffectiveness'),
        key: 'control_1_overall_effectiveness_name'
      },
      {
        label: t('info.controlEffectiveness.control1Status'),
        key: 'control_1_status_name'
      },
      {
        label: t('info.controlEffectiveness.control1Owner'),
        key: 'control_1_owner_name'
      },
      {
        label: t('info.controlEffectiveness.control2DesignEffectiveness'),
        key: 'control_2_design_effectiveness_name'
      },
      {
        label: t('info.controlEffectiveness.control2LastTestDateDesign'),
        key: 'control_2_last_test_date_design'
      },
      {
        label: t('info.controlEffectiveness.control2PerformerTOD'),
        key: 'control_2_performer_tod'
      },
      {
        label: t('info.controlEffectiveness.control2Approver'),
        key: 'control_2_approver'
      },
      {
        label: t('info.controlEffectiveness.control3OperatingEffectiveness'),
        key: 'control_3_operating_effectiveness_name'
      },
      {
        label: t('info.controlEffectiveness.control3LastTestDateOperational'),
        key: 'control_3_last_test_date_operational'
      },
      {
        label: t('info.controlEffectiveness.control3NextTestDate'),
        key: 'control_3_next_test_date'
      },
      {
        label: t('info.controlEffectiveness.control3TestResult'),
        key: 'control_3_test_result_name'
      },
      {
        label: t('info.controlEffectiveness.control4AttestationEffectiveness'),
        key: 'control_4_attestation_effectiveness_name'
      },
      {
        label: t('info.controlEffectiveness.control4ControlEffectiveness'),
        key: 'control_4_control_effectiveness_name'
      },
      {
        label: t('info.controlEffectiveness.control4TestStatus'),
        key: 'control_4_test_status_name'
      }
    ]
  }
});

const getNestedValue = (obj: any, path: string): any => {
  return path.split('_').reduce((current, part) => current?.[part], obj);
};

export const InfoPopover = ({
  type,
  item
}: {
  type: InfoType;
  item: Record<string, any>;
}) => {
  const { t } = useTranslation();

  const config = infoConfig(t)[type];

  return (
    <Styled.PopoverContent>
      {config.fields.map((field) => {
        let value = item[field.key];

        // Handle nested object access for controlEffectiveness type
        if (type === 'controlEffectiveness' && !value) {
          value = getNestedValue(item, field.key);
        }

        return (
          <p key={field.key}>
            <b>{field.label}:</b>{' '}
            {field.transform
              ? field.transform(value)
              : Array.isArray(value)
              ? value.join(', ')
              : value ?? '-'}
          </p>
        );
      })}
    </Styled.PopoverContent>
  );
};
